import type {
  CaseDetail,
  CaseHolding,
  CaseHoldingCategory,
  CasePrescriptionStep,
  CaseRecord,
  CaseTrade,
  CaseTradeSegment,
  CaseTradeSide,
  FlowType,
  Holding,
  HoldingCategory,
  PrescriptionStep,
  ServiceType,
  Trade,
  TradeSegment,
} from '@/features/cases/types'

/** Local copy of `use-my-case-detail.ts`'s `SERVICE_TYPE_TO_FLOW` — not imported from there to
 * avoid pulling a `'use client'` hooks file (and its `@/features/investors` dependency) into this
 * plain data-adapter module. */
const SERVICE_TYPE_TO_FLOW: Record<ServiceType, FlowType> = {
  portfolio_audit: 'audit',
  trades_audit: 'trades',
  build_portfolio: 'build',
}

const HOLDING_CATEGORY_MAP: Record<CaseHoldingCategory, HoldingCategory> = {
  equity: 'Equity',
  debt: 'Debt',
  hybrid: 'Hybrid',
  gold: 'Gold',
}

const TRADE_SEGMENT_MAP: Record<CaseTradeSegment, TradeSegment> = {
  equity: 'Equity',
  fno: 'F&O',
  commodity: 'Commodity',
}

const TRADE_SIDE_MAP: Record<CaseTradeSide, 'BUY' | 'SELL'> = { buy: 'BUY', sell: 'SELL' }

/** Bridges real `CaseDetail` data into the shape `CaseAnalysis`/`CasePrescription`/`CaseDocuments`
 * already expect (`CaseRecord`) — those three components are shared between the real staff/admin
 * `client-detail.tsx` path and the real investor-dashboard path, and can't have their own prop
 * types changed, so this adapter lives here instead, at the one shared boundary. */
export function toMockCaseRecord(detail: CaseDetail): CaseRecord {
  return {
    id: detail.id,
    reference: detail.reference,
    investorId: detail.investor?.id ?? '',
    flow: SERVICE_TYPE_TO_FLOW[detail.serviceType],
    status: detail.caseStatus,
    wealthScore: detail.wealthScore ?? 0,
    staffId: detail.advisor?.id ?? null,
    updatedAt: detail.updatedAt,
    consultationSlot: detail.consultationSlotAt ?? undefined,
    leadStatus: detail.seriousnessStatus ?? 'pending',
    note: detail.note ?? undefined,
    credentialsSent: !!detail.loginDetailsSentAt,
  }
}

export function toMockHoldings(items: CaseHolding[], caseId: string): Holding[] {
  return items.map((h) => ({
    id: h.id,
    caseId,
    scheme: h.scheme,
    category: HOLDING_CATEGORY_MAP[h.category],
    units: Number(h.units),
    investedValue: Number(h.investedValue),
    currentValue: Number(h.currentValue),
    xirr: Number(h.xirr),
  }))
}

export function toMockTrades(items: CaseTrade[], caseId: string): Trade[] {
  return items.map((t) => ({
    id: t.id,
    caseId,
    symbol: t.symbol,
    segment: TRADE_SEGMENT_MAP[t.segment],
    side: TRADE_SIDE_MAP[t.side],
    qty: Number(t.qty),
    price: Number(t.price),
    tradedAt: t.tradedAt,
    pnl: Number(t.pnl),
  }))
}

export function toMockSteps(items: CasePrescriptionStep[]): PrescriptionStep[] {
  return items.map((s) => ({
    id: s.id,
    caseId: '',
    text: s.description,
    title: s.title,
    createdAt: '',
    priority: s.priority,
    impactPoints: s.impactPoints,
    done: s.done,
    doneByInvestor: s.doneByInvestor,
  }))
}
